import { Deserializer, Serializer } from 'jsonapi-serializer';
import { JsonApiResponse } from 'types/store/jsonApi';
import { LdTeam } from 'types/store/normalized/ldTeam';
import { BusinessUnitSummary } from 'types/store/normalized/businessUnit';
import { ProgramSummary } from 'types/store/normalized/programSummary';
import {
  ProjectProcess,
  ProcessStage,
} from 'types/store/normalized/projectProcess';
import {
  Project,
  ProjectOwner,
  ProjectWithOwner,
  FetchProjectsListParams,
} from 'types/store/projectsList';
import {
  BASE_URL,
  ACTIVE_PROJECTS_FIELD_PARAM,
  QuickFilter,
  ACTIVE_PROJECTS_STATUSES,
  DATE_RANGE_FILTER_KEYS,
  isDateRangeFilterType,
} from 'utils/constants/projectsList';
import { ProjectPermission } from 'utils/types/projectsList';

interface DeserializedData
  extends Omit<
    Project,
    | 'owners'
    | 'teams'
    | 'businessTeams'
    | 'programs'
    | 'process'
    | 'stage'
    | 'permissions'
  > {
  owners?: ProjectOwner[];
  teams?: LdTeam[];
  businessTeams?: BusinessUnitSummary[];
  programs?: ProgramSummary[];
  process?: ProjectProcess;
  stage?: ProcessStage;
  permissions?: ProjectPermission;
}

interface NormalizedData {
  projects: Project[];
  projectOwners: ProjectOwner[];
  ldTeams: LdTeam[];
  businessTeams: BusinessUnitSummary[];
  programs: ProgramSummary[];
  processes: ProjectProcess[];
  stages: ProcessStage[];
  permissions: Record<string, ProjectPermission>;
}

export const deserialize: (
  jsonApiResponse: JsonApiResponse<Project>
) => Promise<DeserializedData[]> = async (jsonApiResponse) => {
  const deserializer = new Deserializer({
    keyForAttribute: 'camelCase',
    transform: (record: JsonApiResponse<Project>) => {
      delete record['links'];
      delete record['meta'];
      return record;
    },
  });
  return await deserializer.deserialize(jsonApiResponse);
};

export const serialize: (
  data: ProjectWithOwner[],
  links?: Record<string, string>,
  meta?: Record<string, string | number>
) => Promise<JsonApiResponse<Project>> = async (
  data,
  links = {},
  meta = {}
) => {
  const serializer = new Serializer('projects', {
    attributes: [
      'id',
      'organizationId',
      'displayId',
      'name',
      'status',
      'priority',
      'owners',
      'health',
      'teams',
      'category',
      'startDate',
      'targetCompletionDate',
      'resourcingType',
      'process',
      'stage',
      'permissions',
      'isArchived',
      'taskCount',
      'completedTaskCount',
    ],
    owners: {
      ref: 'id',
      included: true,
      attributes: ['id', 'name', 'email', 'status', 'avatarUrl'],
    },
    topLevelLinks: links,
    meta,
  });
  return await serializer.serialize(data);
};

export const normalize: (data: DeserializedData[]) => NormalizedData = (
  data
) => {
  const projects = new Map<string, Project>();
  const projectOwners = new Map<string, ProjectOwner>();
  const ldTeams = new Map<string, LdTeam>();
  const businessTeamsMap = new Map<string, BusinessUnitSummary>();
  const programsMap = new Map<string, ProgramSummary>();
  const processesMap = new Map<string, ProjectProcess>();
  const stagesMap = new Map<string, ProcessStage>();
  const permissionsMap = new Map<string, ProjectPermission>();
  for (const item of data) {
    const {
      owners = [],
      teams = [],
      businessTeams = [],
      programs = [],
      process,
      stage,
      permissions,
      ...props
    } = item;
    projects.set(item.id, {
      ...props,
      owners: owners.map((o) => o.id),
      teams: teams ? teams.map((t) => t.id) : [],
      businessTeams: businessTeams ? businessTeams.map((t) => t.id) : [],
      programs: programs ? programs.map((program) => program.id) : [],
      process: process ? process.id : '',
      stage: stage ? stage.id : '',
    });
    if (permissions) {
      permissionsMap.set(item.id, permissions);
    }
    for (const owner of owners) {
      projectOwners.set(owner.id, owner);
    }
    if (teams) {
      for (const team of teams) {
        ldTeams.set(team.id, team);
      }
    }
    if (businessTeams) {
      for (const businessTeam of businessTeams) {
        businessTeamsMap.set(businessTeam.id, businessTeam);
      }
    }
    if (programs) {
      for (const program of programs) {
        programsMap.set(program.id, program);
      }
    }
    if (process) {
      processesMap.set(process.id, process);
    }
    if (stage) {
      stagesMap.set(stage.id, stage);
    }
  }
  return {
    projects: Array.from(projects.values()),
    projectOwners: Array.from(projectOwners.values()),
    ldTeams: Array.from(ldTeams.values()),
    businessTeams: Array.from(businessTeamsMap.values()),
    programs: Array.from(programsMap.values()),
    processes: Array.from(processesMap.values()),
    stages: Array.from(stagesMap.values()),
    permissions: Object.fromEntries(permissionsMap.entries()),
  };
};

export const buildJsonApiUrl: (
  params: FetchProjectsListParams,
  isConsolidatedView?: boolean
) => string = (params, isConsolidatedView = false) => {
  const searchParams = new URLSearchParams();
  if (params.filter) {
    Object.entries(params.filter).forEach(([key, values]) => {
      if (Array.isArray(values)) {
        values.forEach((value) => {
          searchParams.append(`filter[${key}][]`, value.toString());
        });
        return;
      }

      if (DATE_RANGE_FILTER_KEYS.includes(key)) {
        if (isDateRangeFilterType(values)) {
          if (values.min) {
            searchParams.append(`filter[${key}][min]`, values.min as string);
          }
          if (values.max) {
            searchParams.append(`filter[${key}][max]`, values.max as string);
          }
        }
        return;
      }

      if (values) {
        searchParams.append(`filter[${key}]`, values as string);
      }
    });
  }

  if (params.quickFilters) {
    if (Array.isArray(params.quickFilters) && params.quickFilters.length > 0) {
      const openQuickFilterApplied = params.quickFilters.includes(
        QuickFilter.OPEN_PROJECTS
      );
      params.quickFilters.forEach((quickFilter) => {
        searchParams.append('quickFilter[]', quickFilter);
      });
      if (openQuickFilterApplied && !params.filter?.status) {
        ACTIVE_PROJECTS_STATUSES.forEach((status) => {
          searchParams.append('filter[status][]', status.toString());
        });
      }
    }
  }

  if (!params.quickFilters && !params.filter?.status && !isConsolidatedView) {
    ACTIVE_PROJECTS_STATUSES.forEach((status) => {
      searchParams.append('filter[status][]', status.toString());
    });
  }

  if (params.search) {
    searchParams.append('filter[name]', params.search);
    searchParams.append('filter[displayId]', params.search);
  }

  if (params.page) {
    searchParams.append('page[limit]', params.page.limit.toString());
    if (params.page.cursor) {
      searchParams.append('page[cursor]', params.page.cursor);
    }
  } else {
    searchParams.append('page[limit]', '100');
  }

  if (params.sort) {
    searchParams.append('sort', params.sort);
  } else {
    searchParams.append('sort', '-updatedAt');
  }

  if (params.field) {
    searchParams.append('field[project]', params.field);
  } else {
    searchParams.append('field[project]', ACTIVE_PROJECTS_FIELD_PARAM);
  }

  if (params.include) {
    searchParams.append('include', params.include);
  } else {
    searchParams.append('include', 'owners');
  }

  return `${BASE_URL}?${searchParams.toString()}`;
};
